import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('nanokart_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('nanokart_token');
      localStorage.removeItem('nanokart_user');
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/partners/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  // Legacy register (redirects to OTP flow)
  register: (data) => api.post('/api/auth/register', data),
  // OTP-based registration flow
  registerSendOtp: (data) => api.post('/api/auth/register/send-otp', data),
  registerVerifyOtp: (data) => api.post('/api/auth/register/verify-otp', data),
  // Password-based login
  login: (data) => api.post('/api/auth/login', data),
  // OTP-based login flow
  loginSendOtp: (data) => api.post('/api/auth/login/send-otp', data),
  loginVerifyOtp: (data) => api.post('/api/auth/login/verify-otp', data),
  // Resend OTP
  resendOtp: (data) => api.post('/api/auth/resend-otp', data),
  // Current user
  getMe: () => api.get('/api/auth/me'),
  // Referral validation (public)
  validateReferrer: (email) => api.get(`/api/partners/referral/validate?referrer_email=${encodeURIComponent(email)}`),
  // Google OAuth
  googleSession: (data) => api.post('/api/auth/google/session', data),
  googleLinkStatus: () => api.get('/api/auth/google/link-status'),
  googleUnlink: () => api.post('/api/auth/google/unlink'),
};

// Partner APIs
export const partnerAPI = {
  getProfile: () => api.get('/api/partners/profile'),
  updateProfile: (data) => api.put('/api/partners/profile', data),
  // Password change
  changePassword: (data) => api.post('/api/partners/change-password', data),
  sendPasswordChangeOtp: () => api.post('/api/partners/change-password/send-otp'),
  verifyPasswordChangeOtp: (data) => api.post('/api/partners/change-password/verify-otp', data),
  // Phone change with OTP
  sendPhoneChangeOtp: (data) => api.post('/api/partners/change-phone/send-otp', data),
  verifyPhoneChangeOtp: (data) => api.post('/api/partners/change-phone/verify-otp', data),
  // Onboarding
  getOnboarding: () => api.get('/api/partners/onboarding'),
  dismissOnboarding: () => api.post('/api/partners/onboarding/dismiss'),
  resetOnboarding: () => api.post('/api/partners/onboarding/reset'),
  // Referral Program
  getReferralInfo: () => api.get('/api/partners/referral'),
  sendReferralInvitation: (data) => api.post('/api/partners/referral/invite', data),
  getReferralInvitations: () => api.get('/api/partners/referral/invitations'),
  // Logo Management
  uploadLogo: (formData) => api.post('/api/partners/logo', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deleteLogo: () => api.delete('/api/partners/logo'),
  getLogo: () => api.get('/api/partners/logo'),
};

// Team Management APIs (org sub-admins & users with own logins)
export const teamAPI = {
  getContext: () => api.get('/api/team/context'),
  listUsers: () => api.get('/api/team/users'),
  createUser: (data) => api.post('/api/team/users', data),
  updateUser: (id, data) => api.put(`/api/team/users/${id}`, data),
  setPassword: (id, data) => api.post(`/api/team/users/${id}/password`, data),
  setStatus: (id, data) => api.post(`/api/team/users/${id}/status`, data),
  deleteUser: (id) => api.delete(`/api/team/users/${id}`),
  listGroups: () => api.get('/api/team/groups'),
  createGroup: (data) => api.post('/api/team/groups', data),
  updateGroup: (id, data) => api.put(`/api/team/groups/${id}`, data),
  deleteGroup: (id) => api.delete(`/api/team/groups/${id}`),
};

// Dashboard APIs
export const dashboardAPI = {
  getStats: () => api.get('/api/dashboard/stats'),
};

// TryOn APIs
export const tryonAPI = {
  // File upload based TryOn (real Maahi API)
  execute: (formData, source = 'API') => api.post('/api/v1/tryon', formData, {
    headers: { 'Content-Type': 'multipart/form-data', 'X-Source': source }
  }),
  // URL-based TryOn (downloads images first)
  executeUrl: (data, source = 'API') => api.post('/api/v1/tryon/url', data, {
    headers: { 'X-Source': source }
  }),
  // Mixed: human file + garment URL
  executeWithUrl: (formData, source = 'API') => api.post('/api/v1/tryon/mixed', formData, {
    headers: { 'Content-Type': 'multipart/form-data', 'X-Source': source }
  }),
};

// Moments APIs
export const momentsAPI = {
  // JSON-based Moments (main endpoint)
  execute: (data, source = 'API') => api.post('/api/v1/moments', data, {
    headers: { 'X-Source': source }
  }),
};

// Soft Delete API
export const deleteAPI = {
  softDelete: (data) => api.post('/api/v1/results/softdelete', data),
};

// Gallery APIs
export const galleryAPI = {
  getItems: (params) => api.get('/api/gallery', { params }),
  getItemDetails: (requestId) => api.get(`/api/gallery/${requestId}`),
  createShareLink: (cacheKey) => api.post(`/api/gallery/${cacheKey}/share`),
  disableShareLink: (cacheKey) => api.delete(`/api/gallery/${cacheKey}/share`),
  createTicket: (data) => api.post('/api/gallery/ticket', data),
};

// Shareable Link API (public)
export const shareAPI = {
  getShared: (token) => api.get(`/api/share/${token}`),
};

// Wallet APIs
export const walletAPI = {
  get: () => api.get('/api/wallet'),
  getSummary: () => api.get('/api/wallet/summary'),
  getSavingsCalculator: () => api.get('/api/wallet/savings-calculator'),
  recharge: (data) => api.post('/api/wallet/recharge', data),
  getCreditPackages: () => api.get('/api/credit-packages'),
  getTransactions: (params) => api.get('/api/transactions', { params }),
  exportTransactions: (params) => api.get('/api/transactions/export', { params }),
};

// Subscription APIs
export const subscriptionAPI = {
  getPlans: () => api.get('/api/subscriptions/plans'),
  getCurrent: () => api.get('/api/subscriptions/current'),
  checkFeature: (feature) => api.get(`/api/subscriptions/feature-check/${feature}`),
  getStatus: () => api.get('/api/subscription/status'),
};

// Razorpay Payment APIs
export const paymentAPI = {
  // Legacy endpoints (deprecated, kept for backwards compatibility)
  createOrder: (data) => api.post('/api/razorpay/create-order', data),
  verifyPayment: (data) => api.post('/api/razorpay/verify-payment', data),
  
  // New payment endpoints
  getCreditPacks: () => api.get('/api/payments/credit-packs'),
  getSubscriptionLinks: () => api.get('/api/payments/subscription-links'),
  createTopupOrder: (data) => api.post('/api/payments/create-topup-order', data),
  verifyTopup: (data) => api.post('/api/payments/verify', data),
  getHistory: () => api.get('/api/payments/history'),
  getWalletLedger: (limit = 50) => api.get(`/api/payments/wallet-ledger?limit=${limit}`),
};

// Advanced Settings APIs
export const advancedSettingsAPI = {
  get: () => api.get('/api/partners/advanced-settings'),
  update: (data) => api.put('/api/partners/advanced-settings', data),
};

// API Keys APIs
export const apiKeysAPI = {
  get: () => api.get('/api/api-keys'),
  regenerate: () => api.post('/api/api-keys/regenerate'),
};

// Audit Logs APIs
export const auditAPI = {
  getLogs: (params) => api.get('/api/audit-logs', { params }),
  getSummary: (params) => api.get('/api/audit-logs/summary', { params }),
};

// Customers APIs (ROI & Analytics)
export const customersAPI = {
  getOverview: (days = 30) => api.get('/api/customers/overview', { params: { days } }),
  getTop: (days = 30, limit = 10) => api.get('/api/customers/top', { params: { days, limit } }),
  getList: (params) => api.get('/api/customers/list', { params }),
  getChannelMix: (days = 30) => api.get('/api/customers/channel-mix', { params: { days } }),
  recordPurchaseIntent: (data) => api.post('/api/customers/purchase-intent', data),
  getPurchaseIntents: (params) => api.get('/api/customers/purchase-intents', { params }),
  exportCustomers: (days = 30) => api.post('/api/customers/export', null, { params: { days } }),
};

// Support APIs
export const supportAPI = {
  getTickets: () => api.get('/api/support/tickets'),
  createTicket: (data) => api.post('/api/support/tickets', data),
  getTicket: (id) => api.get(`/api/support/tickets/${id}`),
  replyToTicket: (id, data) => api.post(`/api/support/tickets/${id}/reply`, data),
};

// Admin APIs
export const adminAPI = {
  getPartners: (params) => api.get('/api/admin/partners', { params }),
  getPartner: (id) => api.get(`/api/admin/partners/${id}`),
  updatePartner: (id, data) => api.put(`/api/admin/partners/${id}`, data),
  togglePartnerStatus: (id) => api.post(`/api/admin/partners/${id}/toggle-status`),
  adjustWallet: (id, data) => api.post(`/api/admin/partners/${id}/wallet-adjust`, data),
  impersonatePartner: (id) => api.post(`/api/admin/partners/${id}/impersonate`),
  getStats: () => api.get('/api/admin/stats'),
  getDashboardAnalytics: (params) => api.get('/api/admin/dashboard-analytics', { params }),
  getTickets: () => api.get('/api/admin/tickets'),
  getTicket: (id) => api.get(`/api/admin/tickets/${id}`),
  updateTicket: (id, data) => api.put(`/api/admin/tickets/${id}`, data),
  
  // Payment Management
  getPayments: (params = {}) => api.get('/api/admin/payments', { params }),
  getUnmappedPayments: () => api.get('/api/admin/payments/unmapped'),
  mapPayment: (data) => api.post('/api/admin/payments/map', data),
  getCreditPacksConfig: () => api.get('/api/admin/payment-config/credit-packs'),
  updateCreditPacksConfig: (data) => api.put('/api/admin/payment-config/credit-packs', data),
  getSubscriptionLinksConfig: () => api.get('/api/admin/payment-config/subscription-links'),
  updateSubscriptionLinksConfig: (data) => api.put('/api/admin/payment-config/subscription-links', data),
  getWebhookLogs: (limit = 50) => api.get(`/api/admin/webhook-logs?limit=${limit}`),
  // Payment settings (min recharge amount)
  getPaymentSettings: () => api.get('/api/admin/payment-settings'),
  updatePaymentSettings: (data) => api.put('/api/admin/payment-settings', data),
  
  // System Configuration
  getSystemConfig: () => api.get('/api/admin/system-config'),
  getSystemConfigValue: (key) => api.get(`/api/admin/system-config/${key}`),
  updateSystemConfig: (key, data) => api.put(`/api/admin/system-config/${key}`, data),
  
  // Subscription Plans Management
  getSubscriptionPlans: () => api.get('/api/admin/subscription-plans'),
  getSubscriptionPlan: (planId) => api.get(`/api/admin/subscription-plans/${planId}`),
  updateSubscriptionPlan: (planId, data) => api.put(`/api/admin/subscription-plans/${planId}`, data),
  createSubscriptionPlan: (data) => api.post('/api/admin/subscription-plans', data),
  deleteSubscriptionPlan: (planId) => api.delete(`/api/admin/subscription-plans/${planId}`),
  
  // Partner Pricing Overrides
  getPartnerPricing: (partnerId) => api.get(`/api/admin/partners/${partnerId}/pricing`),
  updatePartnerPricing: (partnerId, data) => api.put(`/api/admin/partners/${partnerId}/pricing`, data),
  
  // Partner Password Reset
  resetPartnerPassword: (partnerId, data) => api.post(`/api/admin/partners/${partnerId}/reset-password`, data),
  
  // Sub-Admin Management
  getSubAdmins: () => api.get('/api/admin/sub-admins'),
  getSubAdmin: (adminId) => api.get(`/api/admin/sub-admins/${adminId}`),
  createSubAdmin: (data) => api.post('/api/admin/sub-admins', data),
  updateSubAdmin: (adminId, data) => api.put(`/api/admin/sub-admins/${adminId}`, data),
  deleteSubAdmin: (adminId) => api.delete(`/api/admin/sub-admins/${adminId}`),
  resetSubAdminPassword: (adminId, data) => api.post(`/api/admin/sub-admins/${adminId}/reset-password`, data),
  getAvailablePermissions: () => api.get('/api/admin/permissions'),
  getMyPermissions: () => api.get('/api/admin/my-permissions'),
};

// Public Config API (no auth required)
export const configAPI = {
  getSignupConfig: () => api.get('/api/admin/public/signup-config'),
};

// Analytics APIs (Basic)
export const analyticsAPI = {
  // Basic analytics
  getUsage: (days = 30) => api.get(`/api/analytics/usage?days=${days}`),
  getCredits: (days = 30) => api.get(`/api/analytics/credits?days=${days}`),
  exportUsageCSV: (days = 30) => api.get(`/api/analytics/export/usage?days=${days}`, { responseType: 'blob' }),
  exportTransactionsCSV: (days = 30) => api.get(`/api/analytics/export/transactions?days=${days}`, { responseType: 'blob' }),
  
  // Try-On Analytics Dashboard (Premium/Enterprise)
  checkAccess: () => api.get('/api/analytics/access'),
  getSummary: (params) => api.get('/api/analytics/summary', { params }),
  getSKU: (params) => api.get('/api/analytics/sku', { params }),
  getFunnel: (params) => api.get('/api/analytics/funnel', { params }),
  getMoments: (params) => api.get('/api/analytics/moments', { params }),
  getCustomers: (params) => api.get('/api/analytics/customers', { params }),
  getInventory: (params) => api.get('/api/analytics/inventory', { params }),
  getCost: (params) => api.get('/api/analytics/cost', { params }),
  getQuality: (params) => api.get('/api/analytics/quality', { params }),
  export: (params) => api.get('/api/analytics/export', { params, responseType: 'text' }),
  exportUsageCSV: (days = 30) => api.get('/api/analytics/export/usage', { params: { days }, responseType: 'text' }),
  exportTransactionsCSV: (days = 30) => api.get('/api/analytics/export/transactions', { params: { days }, responseType: 'text' }),
  
  // NEW: Unified tiered analytics endpoint
  getUnified: (days = 30) => api.get('/api/analytics/unified', { params: { days } }),
  
  // Admin analytics
  adminSummary: (params) => api.get('/api/analytics/admin/summary', { params }),
  adminSKU: (params) => api.get('/api/analytics/admin/sku', { params }),
  adminFull: (params) => api.get('/api/analytics/admin/full', { params }),
  
  // Public TryOn Analytics
  publicTryonSummary: () => api.get('/api/analytics/public-tryon/summary'),
  publicTryonSessions: (params) => api.get('/api/analytics/public-tryon', { params }),
  publicTryonExport: (days = 30) => api.get('/api/analytics/public-tryon/export', { params: { days } }),
};

// Membership APIs
export const membershipAPI = {
  getStatus: () => api.get('/api/membership/status'),
};

// Invoice APIs
export const invoiceAPI = {
  download: (transactionId) => api.get(`/api/transactions/${transactionId}/invoice`, { responseType: 'blob' }),
};

// Inventory APIs
// Color Change API
export const colorChangeAPI = {
  getCost: (quality = 'standard') => api.get(`/api/color/cost?quality=${quality}`),
  change: (data) => api.post('/api/color/change', data),
};

export const inventoryAPI = {
  // Products
  listProducts: (params) => api.get('/api/inventory/products', { params }),
  getProduct: (productId) => api.get(`/api/inventory/products/${productId}`),
  createProduct: (data) => api.post('/api/inventory/products', data),
  updateProduct: (productId, data) => api.put(`/api/inventory/products/${productId}`, data),
  deleteProduct: (productId) => api.delete(`/api/inventory/products/${productId}`),
  
  // SKU Limit Check
  checkSkuLimit: () => api.get('/api/subscriptions/sku-limit-check'),
  
  // Product Images
  listImages: (productId) => api.get(`/api/inventory/products/${productId}/images`),
  uploadImage: (productId, formData) => api.post(`/api/inventory/products/${productId}/images`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    // Surface dropped/oversized uploads instead of hanging forever (AWS API
    // Gateway has a ~6 MB sync-invoke cap; client-side compression should keep
    // us well under, but the timeout guarantees we don't spin indefinitely).
    timeout: 60000,
  }),
  setTryonInput: (productId, imageId) => api.put(`/api/inventory/products/${productId}/images/tryon-input`, { image_id: imageId }),
  deleteImage: (productId, imageId) => api.delete(`/api/inventory/products/${productId}/images/${imageId}`),
  
  // Variants
  listVariants: (productId) => api.get(`/api/inventory/products/${productId}/variants`),
  createVariant: (productId, data) => api.post(`/api/inventory/products/${productId}/variants`, data),
  updateVariant: (productId, variantId, data) => api.put(`/api/inventory/products/${productId}/variants/${variantId}`, data),
  deleteVariant: (productId, variantId) => api.delete(`/api/inventory/products/${productId}/variants/${variantId}`),
  adjustStock: (productId, variantId, data) => api.post(`/api/inventory/products/${productId}/variants/${variantId}/adjust-stock`, data),
  
  // Share Links
  listShareLinks: (productId) => api.get(`/api/inventory/products/${productId}/share-links`),
  createShareLink: (productId, data) => api.post(`/api/inventory/products/${productId}/share-links`, data),
  revokeShareLink: (productId, token) => api.delete(`/api/inventory/products/${productId}/share-links/${token}`),
  
  // All share links (partner-wide)
  listAllShareLinks: (params) => api.get('/api/inventory/share-links/all', { params }),
  listAllShareLinksWithStats: (params) => api.get('/api/inventory/share-links/all-with-stats', { params }),
  createShareLinkDirect: (productId, data) => api.post(`/api/inventory/share-links?product_id=${productId}`, data),
  toggleShareLinkStatus: (token) => api.patch(`/api/inventory/share-links/${token}/toggle`),
  getShareLinkStats: (token) => api.get(`/api/inventory/share-links/${token}/stats`),
  
  // Customer blocking
  blockCustomer: (customerPhone) => api.post(`/api/inventory/customers/block?customer_phone=${encodeURIComponent(customerPhone)}`),
  unblockCustomer: (customerPhone) => api.post(`/api/inventory/customers/unblock?customer_phone=${encodeURIComponent(customerPhone)}`),
  listBlockedCustomers: () => api.get('/api/inventory/customers/blocked'),
  isCustomerBlocked: (customerPhone) => api.get(`/api/inventory/customers/${encodeURIComponent(customerPhone)}/is-blocked`),
  
  // Bulk Upload
  downloadTemplate: () => api.get('/api/inventory/bulk/template', { responseType: 'blob' }),
  getUploadInstructions: () => api.get('/api/inventory/bulk/instructions'),
  validateCSV: (formData) => api.post('/api/inventory/bulk/validate', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  bulkUpload: (formData) => api.post('/api/inventory/bulk/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 300000 // 5 minutes timeout for bulk upload
  }),

  // Import from URL (3-tier crawler)
  importDiscover: (url) => api.post('/api/inventory/import/discover', { url }),
  importSitemap: (url, maxUrls = 500) => api.post('/api/inventory/import/sitemap', { url, max_urls: maxUrls, only_product_urls: true }, { timeout: 60000 }),
  importEstimateCost: (urls) => api.post('/api/inventory/import/estimate-cost', { urls }),
  importReserve: (urls, sourceUrl) => api.post('/api/inventory/import/reserve', { urls, source_url: sourceUrl || null }),
  importExtractBatch: (urls, reservationId) => api.post('/api/inventory/import/extract-batch', { urls, reservation_id: reservationId || null }, {
    timeout: 60000
  }),
  importSettle: (reservationId, actualTier3Calls) => api.post('/api/inventory/import/settle', {
    reservation_id: reservationId, actual_tier3_calls: actualTier3Calls,
  }),
  importSaveBatch: (products) => api.post('/api/inventory/import/save-batch', { products }, {
    timeout: 120000
  }),
};

// Public Try-On API (no auth required)
export const publicTryonAPI = {
  getTryonData: (token) => api.get(`/api/public/tryon/${token}`),
  executeTryon: (token, formData) => api.post(`/api/public/tryon/${token}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
};

// Operations Ledger API
export const operationsAPI = {
  // Partner operations
  list: (params) => api.get('/api/operations', { params }),
  getDetail: (operationId) => api.get(`/api/operations/${operationId}`),
  getPresignedUrl: (operationId, artifactId) => api.get(`/api/operations/${operationId}/presign`, { params: { artifact_id: artifactId } }),
  createTicket: (operationId, data) => api.post(`/api/operations/${operationId}/ticket`, data),
  
  // Admin operations
  adminList: (params) => api.get('/api/admin/operations', { params }),
  adminGetDetail: (operationId) => api.get(`/api/admin/operations/${operationId}`),
  adminGetPresignedUrl: (operationId, artifactId) => api.get(`/api/admin/operations/${operationId}/presign`, { params: { artifact_id: artifactId } }),
};

// Public APIs
export const publicAPI = {
  contact: (data) => api.post('/api/contact', data),
  requestAccess: (data) => api.post('/api/request-access', data),
};

// Public Studio APIs (for In-Store TryOn)
export const publicStudioAPI = {
  // Partner endpoints (auth required)
  createLink: (data) => api.post('/api/public-studio/links', data),
  getLinks: () => api.get('/api/public-studio/links'),
  toggleLink: (linkId) => api.patch(`/api/public-studio/links/${linkId}/toggle`),
  regenerateLink: (linkId) => api.post(`/api/public-studio/links/${linkId}/regenerate`),
  deleteLink: (linkId) => api.delete(`/api/public-studio/links/${linkId}`),
  
  // Public customer endpoints (no auth)
  validateLink: (token) => axios.get(`${API_BASE_URL}/api/public-studio/validate/${token}`),
  sendOtp: (data) => axios.post(`${API_BASE_URL}/api/public-studio/auth/send-otp`, data),
  verifyOtp: (data) => axios.post(`${API_BASE_URL}/api/public-studio/auth/verify-otp`, data),
  resendOtp: (data) => axios.post(`${API_BASE_URL}/api/public-studio/auth/resend-otp`, data),
  
  // Session endpoints (session token required)
  getInventory: (sessionToken, params) => axios.get(`${API_BASE_URL}/api/public-studio/session/inventory`, { 
    params: { session_token: sessionToken, ...params } 
  }),
  getProduct: (productId, sessionToken) => axios.get(`${API_BASE_URL}/api/public-studio/session/product/${productId}`, {
    params: { session_token: sessionToken }
  }),
  getSessionStatus: (sessionToken) => axios.get(`${API_BASE_URL}/api/public-studio/session/status`, {
    params: { session_token: sessionToken }
  }),
  uploadPhoto: (sessionToken, formData) => axios.post(`${API_BASE_URL}/api/public-studio/session/upload-photo?session_token=${sessionToken}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  
  // Cart endpoints
  getCarts: (sessionToken) => axios.get(`${API_BASE_URL}/api/public-studio/session/carts`, {
    params: { session_token: sessionToken }
  }),
  addToSoftCart: (sessionToken, data) => axios.post(`${API_BASE_URL}/api/public-studio/session/soft-cart/add?session_token=${sessionToken}`, data),
  addToHardCart: (sessionToken, data) => axios.post(`${API_BASE_URL}/api/public-studio/session/hard-cart/add?session_token=${sessionToken}`, data),
  removeFromHardCart: (sessionToken, productId, params) => axios.delete(`${API_BASE_URL}/api/public-studio/session/hard-cart/${productId}`, {
    params: { session_token: sessionToken, ...params }
  }),
  updateCartQuantity: (sessionToken, productId, quantity, params) => axios.patch(`${API_BASE_URL}/api/public-studio/session/hard-cart/${productId}/quantity`, null, {
    params: { session_token: sessionToken, quantity, ...params }
  }),
  
  // Rate limits (customer)
  getRateLimits: (sessionToken) => axios.get(`${API_BASE_URL}/api/public-studio/session/rate-limits`, {
    params: { session_token: sessionToken }
  }),
  
  // Privacy (customer)
  getPhotoAccessRequests: (sessionToken) => axios.get(`${API_BASE_URL}/api/public-studio/session/photo-access-requests`, {
    params: { session_token: sessionToken }
  }),
  respondToPhotoAccess: (sessionToken, data) => axios.post(`${API_BASE_URL}/api/public-studio/session/respond-photo-access?session_token=${sessionToken}`, data),
  deleteMyData: (sessionToken) => axios.delete(`${API_BASE_URL}/api/public-studio/session/my-data`, {
    params: { session_token: sessionToken }
  }),
  
  // Staff endpoints (auth required)
  searchCustomer: (params) => api.get('/api/public-studio/staff/search-customer', { params }),
  getStaffSession: (sessionId) => api.get(`/api/public-studio/staff/session/${sessionId}`),
  processCheckout: (sessionId, data) => api.post(`/api/public-studio/staff/session/${sessionId}/checkout`, data),
  getAllSessions: (params) => api.get('/api/public-studio/staff/sessions', { params }),
  
  // Analytics (partner)
  getAnalytics: (params) => api.get('/api/public-studio/staff/analytics', { params }),
  
  // Privacy (staff)
  requestPhotoAccess: (data) => api.post('/api/public-studio/staff/request-photo-access', data),
  getSessionPhotos: (sessionId) => api.get(`/api/public-studio/staff/session/${sessionId}/photos`),
  
  // Settings (partner)
  getInstoreSettings: () => api.get('/api/public-studio/partner/instore-settings'),
  updateInstoreSettings: (data) => api.patch('/api/public-studio/partner/instore-settings', data),
  
  // Fixed In-Store Link (partner)
  getFixedLink: () => api.get('/api/public-studio/fixed-link'),
  toggleFixedLink: () => api.patch('/api/public-studio/fixed-link/toggle'),
  
  // Customer Tryon Limit Override (partner)
  setCustomerTryonLimit: (data) => api.post('/api/public-studio/customer-override/tryon-limit', data),
  removeCustomerTryonLimit: (phone) => api.delete(`/api/public-studio/customer-override/tryon-limit/${phone}`),
  getCustomerOverrides: () => api.get('/api/public-studio/customer-overrides'),
};

// SmartGate / Customer Wallet APIs
export const smartgateAPI = {
  getSettings: () => api.get('/api/smartgate/settings'),
  updateSettings: (data) => api.put('/api/smartgate/settings', data),
  listWallets: (params) => api.get('/api/smartgate/wallets', { params }),
  createWallet: (data) => api.post('/api/smartgate/wallets', data),
  getWallet: (customerId) => api.get(`/api/smartgate/wallets/${customerId}`),
  creditWallet: (customerId, data) => api.post(`/api/smartgate/wallets/${customerId}/credit`, data),
  debitWallet: (customerId, data) => api.post(`/api/smartgate/wallets/${customerId}/debit`, data),
  getTransactions: (customerId, params) => api.get(`/api/smartgate/wallets/${customerId}/transactions`, { params }),
  rewardPurchase: (customerId, data) => api.post(`/api/smartgate/wallets/${customerId}/reward-purchase`, data),
  getAnalytics: () => api.get('/api/smartgate/analytics'),
};

export default api;
